import { Alert, Button, Col, DatePicker, Input, Row, Switch, Typography, message } from "antd"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons"
import { FlexCol, FlexRow, UnderlinedSectionTitle } from "../../components/commons-ts/common"
import { Translated } from "../../utils/translated"
import dayjs from "dayjs"
import { Label } from "../../components/commons-ts/input"
import { useUsercommContextBLE } from "../../usercomm/local/ble/usercommProviderBLE"
import { StationRestartWidget } from "../../components/commons-ts/stationRestartWidget"
import {
  useUsercommGetStationConfigBLE,
  useUsercommGetStationStateBLE,
  useUsercommSetStationConfigBLE,
} from "../../usercomm/local/ble/usercommAsyncRequestBLE"
import { CENTRAL_CONTAINER_GAP } from "../../utils/utils"
import { useSyncronizationContext } from "../../providers/syncronizationProvider"
import { useTranslationContext } from "../../providers/translationProvider"

const HEAD_HIC_BD_ADDR_PREFIX = "00:06:66:"

export const StationConfigSettings: FC = () => {
  const { translated } = useTranslationContext()
  const [antdMessageCtx, antdMessageCtxHolder] = message.useMessage()
  const {} = useUsercommContextBLE()
  const [stationConfig, getStationConfig] = useUsercommGetStationConfigBLE()
  const [setStationConfigAck, setStationConfig] = useUsercommSetStationConfigBLE()

  const { isAdmin } = useSyncronizationContext()

  const [stationState, getStationState] = useUsercommGetStationStateBLE()

  const [lastMetrology, setLastMetrology] = useState<dayjs.Dayjs | null>(null)
  const [nextMetrology, setNextMetrology] = useState<dayjs.Dayjs | null>(null)
  const [serialNumber, setSerialNumber] = useState<string | null>(null)

  const [headHicBdAddr, setHeadHicBdAddr] = useState<string | null>(null)
  const [headHicBdAddrSuffix, setHeadHicBdAddrSuffix] = useState<string | null>(null)

  const [lidarDistanceOffsetCm, setLidarDistanceOffsetCm] = useState<string | null>(null)
  const [buzzerState, setBuzzerState] = useState<boolean>(false)

  useEffect(() => {
    getStationState()
    getStationConfig()
  }, [])

  useEffect(() => {
    if (stationState === null) {
      return
    }
    console.log(`StationConfigSettings: station state`, stationState.toObject())
  }, [stationState])

  useEffect(() => {
    if (stationConfig === null) {
      return
    }
    if (stationConfig.last_metrology_date > 0) {
      setLastMetrology(dayjs(stationConfig.last_metrology_date))
    }
    if (stationConfig.next_metrology_date > 0) {
      setNextMetrology(dayjs(stationConfig.next_metrology_date))
    }
    setLidarDistanceOffsetCm(stationConfig.lidar_distance_offset_cm.toString())
    if (stationConfig.serial_number !== "") {
      setSerialNumber(stationConfig.serial_number)
    }
    if (stationConfig.head_hic_bd_addr !== "") {
      setHeadHicBdAddr(stationConfig.head_hic_bd_addr)
      setHeadHicBdAddrSuffix(stationConfig.head_hic_bd_addr.slice(HEAD_HIC_BD_ADDR_PREFIX.length))
    }
    if (stationConfig.buzzer_state !== undefined) {
      setBuzzerState(stationConfig.buzzer_state)
    }
  }, [stationConfig])

  const onSave = useCallback(() => {
    if (stationConfig === null) {
      return
    }
    let newStationConfig = stationConfig.clone()
    if (lidarDistanceOffsetCm !== null && lidarDistanceOffsetCm !== "") {
      newStationConfig.lidar_distance_offset_cm = parseInt(lidarDistanceOffsetCm)
    }
    if (serialNumber !== null && serialNumber !== "") {
      newStationConfig.serial_number = serialNumber
    }
    if (headHicBdAddr !== null && headHicBdAddr !== "") {
      newStationConfig.head_hic_bd_addr = headHicBdAddr
    }
    if (lastMetrology !== null) {
      newStationConfig.last_metrology_date = lastMetrology.toDate().getTime()
    }
    if (nextMetrology !== null) {
      newStationConfig.next_metrology_date = nextMetrology.toDate().getTime()
    }
    newStationConfig.buzzer_state = buzzerState
    setStationConfig(newStationConfig)
  }, [
    lastMetrology,
    nextMetrology,
    serialNumber,
    headHicBdAddr,
    lidarDistanceOffsetCm,
    buzzerState,
    stationConfig,
  ])

  useEffect(() => {
    if (setStationConfigAck === null) {
      return
    }
    antdMessageCtx.success(translated("Base Station is successfully configured!"))
  }, [setStationConfigAck])

  const memoSerialNumberOrBdAddrChanged = useMemo(() => {
    if (stationConfig === null) {
      return false
    }
    if (serialNumber !== null && serialNumber.localeCompare(stationConfig.serial_number) !== 0) {
      console.log(
        `memoSerialNumberOrBdAddrChanged: serialNumber: "${serialNumber}" !== "${stationConfig.serial_number}"`,
      )
      return true
    }
    if (
      headHicBdAddr !== null &&
      headHicBdAddr.localeCompare(stationConfig.head_hic_bd_addr) !== 0
    ) {
      console.log(
        `memoSerialNumberOrBdAddrChanged: headHicBdAddr: "${headHicBdAddr}" !== "${stationConfig.head_hic_bd_addr}"`,
      )
      return true
    }
    return false
  }, [stationConfig, serialNumber, headHicBdAddr])

  const memoEffectiveRfcommHeadHicBdAddrCandidates = useMemo(() => {
    if (
      stationState === null ||
      stationState.effective_rfcomm_head_hic_bd_addr_candidates === null ||
      stationState.effective_rfcomm_head_hic_bd_addr_candidates.length === 0
    ) {
      return null
    }
    let _effectiveRfcommHeadHicBdAddrCandidatesMap: Record<string, string> = {}
    stationState.effective_rfcomm_head_hic_bd_addr_candidates.forEach((bdAddr) => {
      _effectiveRfcommHeadHicBdAddrCandidatesMap[bdAddr.bd_addr] = bdAddr.device_name
    })
    return _effectiveRfcommHeadHicBdAddrCandidatesMap
  }, [stationState])

  const memoEffectiveRfcommHeadHicBdAddrCandidatesList = useMemo(() => {
    if (memoEffectiveRfcommHeadHicBdAddrCandidates === null) {
      return null
    }
    let listElements: JSX.Element[] = []
    Object.entries(memoEffectiveRfcommHeadHicBdAddrCandidates).forEach(([bdAddr, bdName]) => {
      let el = (
        <li key={bdAddr}>
          <b>{bdAddr}</b> ({bdName})
        </li>
      )
      if (bdAddr === stationConfig?.head_hic_bd_addr) {
        el = (
          <li key={bdAddr}>
            <b>{bdAddr}</b> ({bdName}) <b>[CURRENT]</b>
          </li>
        )
      }
      listElements.push(el)
    })
    return <ul>{listElements}</ul>
  }, [memoEffectiveRfcommHeadHicBdAddrCandidates, stationConfig])

  const memoEffectiveBleClientsList = useMemo(() => {
    if (stationState === null) {
      return null
    }
    let listElements: JSX.Element[] = []
    if (stationState.effective_ble_clients === null) {
      return null
    }
    stationState.effective_ble_clients.forEach((client) => {
      let el = (
        <li key={client.bd_addr}>
          <b>{client.bd_addr}</b> ({client.device_name})
        </li>
      )
      listElements.push(el)
    })
    return <ul>{listElements}</ul>
  }, [stationState])

  return (
    <>
      {antdMessageCtxHolder}
      <FlexCol
        style={{
          width: "100%",
          gap: CENTRAL_CONTAINER_GAP,
        }}
      >
        {/* Header */}
        <FlexCol style={{ gap: 0 }}>
          <Typography.Text
            style={{
              fontSize: "2rem",
            }}
          >
            <Translated keyEn="Station" />
          </Typography.Text>
        </FlexCol>
        {/* State */}
        <FlexRow style={{ justifyContent: "space-between" }}>
          <UnderlinedSectionTitle>
            <Translated keyEn="Station State" />
          </UnderlinedSectionTitle>
          <div>
            <Button
              size="large"
              onClick={() => {
                getStationConfig()
                getStationState()
              }}
              icon={<ReloadOutlined />}
            >
              <span
                style={{
                  textTransform: "uppercase",
                  // fontWeight: "bold",
                }}
              >
                <Translated keyEn="Reload" />
              </span>
            </Button>
          </div>
        </FlexRow>
        <Row gutter={[10, 10]}>
          <Col>
            <FlexCol style={{ gap: 0 }}>
              {stationState !== null ? (
                <>
                  <div>
                    <span>
                      <Translated keyEn="Station MAC (BLE)" />
                      {": "}
                    </span>
                    <b>{stationState.effective_ble_adapter_bd_addr}</b>
                  </div>
                  <div>
                    <span>
                      <Translated keyEn="Station MAC (RFCOMM)" />
                    </span>
                    {": "}
                    <b>{stationState.effective_rfcomm_adapter_bd_addr}</b>
                  </div>
                  {memoEffectiveRfcommHeadHicBdAddrCandidates && (
                    <div>
                      <span>
                        <Translated keyEn="HIC Heads in radius" />
                      </span>
                      {": "}
                      {memoEffectiveRfcommHeadHicBdAddrCandidatesList}
                    </div>
                  )}
                  {memoEffectiveBleClientsList && (
                    <div>
                      <span>
                        <Translated keyEn="Devices connected via BLE" />
                      </span>
                      {": "}
                      {memoEffectiveBleClientsList}
                    </div>
                  )}
                </>
              ) : (
                <div>N/A</div>
              )}
            </FlexCol>
          </Col>
        </Row>
        {/* Config */}
        <UnderlinedSectionTitle>
          <Translated keyEn="Configuration" />
        </UnderlinedSectionTitle>
        <Row gutter={[10, 10]}>
          {/* Admin only */}
          {isAdmin && (
            <>
              <Col xs={12}>
                <FlexCol style={{ gap: 0 }}>
                  <Label>
                    <Translated keyEn="Serial number" />
                  </Label>
                  <Input
                    type="text"
                    value={serialNumber ?? ""}
                    onChange={(e) => setSerialNumber(e.target.value)}
                    size="large"
                    variant="filled"
                  />
                </FlexCol>
              </Col>
              <Col xs={12}>
                <FlexCol style={{ gap: 0 }}>
                  <Label>
                    <Translated keyEn="HIC BD address" />
                  </Label>
                  <FlexCol
                    style={{
                      gap: 0,
                    }}
                  >
                    <Input
                      type="text"
                      value={headHicBdAddrSuffix ?? ""}
                      prefix={HEAD_HIC_BD_ADDR_PREFIX}
                      prefixCls="head-hic-bd-addr"
                      placeholder="1A:2B:3C"
                      onChange={(e) => {
                        setHeadHicBdAddrSuffix(e.target.value)
                        setHeadHicBdAddr(HEAD_HIC_BD_ADDR_PREFIX + e.target.value)
                      }}
                      size="large"
                      variant="filled"
                    />
                    <div
                      style={{
                        fontSize: 9,
                        textDecoration: "italic",
                      }}
                    >
                      {headHicBdAddr}
                    </div>
                  </FlexCol>
                </FlexCol>
              </Col>
              <Col xs={12}>
                <FlexCol style={{ gap: 0 }}>
                  <Label>
                    <Translated keyEn="Last metrology" />
                  </Label>
                  <DatePicker
                    value={lastMetrology}
                    onChange={(date) => setLastMetrology(date)}
                    size="large"
                    style={{ width: "100%" }}
                  />
                </FlexCol>
              </Col>
              <Col xs={12}>
                <FlexCol style={{ gap: 0 }}>
                  <Label>
                    <Translated keyEn="Next metrology" />
                  </Label>
                  <DatePicker
                    value={nextMetrology}
                    onChange={(date) => setNextMetrology(date)}
                    size="large"
                    style={{ width: "100%" }}
                  />
                </FlexCol>
              </Col>
            </>
          )}
          <Col xs={12}>
            <FlexCol style={{ gap: 0 }}>
              <Label>
                <Translated keyEn="Lidar distance offset (cm)" />
              </Label>
              <Input
                type="number"
                value={lidarDistanceOffsetCm ?? ""}
                onChange={(e) => setLidarDistanceOffsetCm(e.target.value)}
                size="large"
                variant="filled"
              />
            </FlexCol>
          </Col>
          <Col xs={12}>
            <FlexCol style={{ gap: 0, alignItems: "start" }}>
              <Label>
                <Translated keyEn="Enable BIP" />
              </Label>
              <Switch
                checked={buzzerState}
                onChange={(checked) => {
                  setBuzzerState(checked)
                }}
              />
            </FlexCol>
          </Col>
          {memoSerialNumberOrBdAddrChanged && (
            <Col xs={24}>
              <Alert
                style={{
                  width: "100%",
                }}
                type="warning"
                message={
                  <FlexRow
                    style={{
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <Translated keyEn={"Station S/N or HIC BD address have changed."} />
                      <br />
                      <Translated keyEn="Please SAVE and RESTART the station for the changes to take effect." />
                    </div>
                  </FlexRow>
                }
              />
            </Col>
          )}
        </Row>
        {/* Save */}
        <FlexRow
          style={{
            alignItems: "center",
            alignSelf: "flex-end",
          }}
        >
          <StationRestartWidget />
          <div
            style={{
              width: 200,
            }}
          >
            <Button type="primary" icon={<SaveOutlined />} size="large" block onClick={onSave}>
              <span
                style={{
                  textTransform: "uppercase",
                }}
              >
                <Translated keyEn="Save" />
              </span>
            </Button>
          </div>
        </FlexRow>
      </FlexCol>
    </>
  )
}
